import { useState, useEffect } from "react"
import {
  X, Info, Languages, Plus, Trash2, Edit, BookOpen, FileText, HelpCircle,
  CheckCircle, AlertCircle, Clock, Calendar, Globe, ChevronDown, ChevronUp,
  Image as ImageIcon, Video, Download, ChevronLeft, Settings, Loader2, GraduationCap
} from "lucide-react"
import { Button } from "../ui/button"
import { api } from "../../utils/apiComponent/apiClient"
import { endpoints } from "../../utils/endpoints"

const SUPPORTED_LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' }
]

export default function InspectorPanel({
  // Entités sélectionnées
  selectedCourse,
  selectedModule,
  selectedLesson,
  selectedBlock,
  selectedQuiz,
  
  // Callbacks
  onClose,
  onEdit,
  onDelete,
  onAddModule,
  onAddBlock,
  onAddQuiz,
  onShowTranslations,
  onLanguageChange, // Nouveau callback pour changer la langue sans ouvrir le panneau de traduction
  onUpdate,
  onEditImage,
  onEditConfiguration,
  
  // États de chargement
  isLoadingQuiz = false,
  isLoadingLesson = false,
  isLoadingBlock = false
}) {
  const [expandedSections, setExpandedSections] = useState({
    info: true,
    translations: false
  })
  const [availableTranslations, setAvailableTranslations] = useState([])
  const [loadingTranslations, setLoadingTranslations] = useState(false)

  // Déterminer l'entité active (priorité: Quiz > Block > Lesson > Module > Course)
  const activeEntity = selectedQuiz || selectedBlock || selectedLesson || selectedModule || selectedCourse
  const entityType = selectedQuiz ? 'quiz' : selectedBlock ? 'block' : selectedLesson ? 'lesson' : selectedModule ? 'module' : selectedCourse ? 'course' : null
  const entityId = activeEntity?.id

  // Charger les traductions disponibles
  useEffect(() => {
    if (!entityId || !entityType) {
      setAvailableTranslations([])
      return
    }

    const loadTranslations = async () => {
      setLoadingTranslations(true)
      try {
        let languages = []
        
        // Récupérer les langues disponibles selon le type
        if (entityType === 'course') {
          try {
            const response = await api.get(endpoints.getCourseLanguages(entityId))
            const allLanguages = response?.data || response || []
            // Filtrer pour ne garder que fr et en
            languages = allLanguages.filter(lang => 
              typeof lang === 'string' && SUPPORTED_LANGUAGES.some(supported => supported.code === lang)
            )
          } catch (err) {
            // Endpoint peut ne pas exister
            languages = []
          }
        } else if (entityType === 'module') {
          try {
            const response = await api.get(endpoints.getModuleLanguages(entityId))
            const allLanguages = response?.data || response || []
            // Filtrer pour ne garder que fr et en
            languages = allLanguages.filter(lang => 
              typeof lang === 'string' && SUPPORTED_LANGUAGES.some(supported => supported.code === lang)
            )
          } catch (err) {
            languages = []
          }
        } else if (entityType === 'lesson') {
          try {
            const response = await api.get(endpoints.getLessonLanguages(entityId))
            const allLanguages = response?.data || response || []
            // Filtrer pour ne garder que fr et en
            languages = allLanguages.filter(lang => 
              typeof lang === 'string' && SUPPORTED_LANGUAGES.some(supported => supported.code === lang)
            )
          } catch (err) {
            languages = []
          }
        } else if (entityType === 'block') {
          try {
            const response = await api.get(endpoints.getBlockLanguages(entityId))
            const allLanguages = response?.data || response || []
            // Filtrer pour ne garder que fr et en
            languages = allLanguages.filter(lang => 
              typeof lang === 'string' && SUPPORTED_LANGUAGES.some(supported => supported.code === lang)
            )
          } catch (err) {
            languages = []
          }
        } else if (entityType === 'quiz') {
          try {
            const response = await api.get(endpoints.getQuizLanguages(entityId))
            // L'API retourne soit un tableau simple de codes de langue : ["en", "fr"]
            // soit un objet avec une propriété languages : { languages: ["en", "fr"], defaultLanguage: "fr" }
            let allLanguages = []
            if (response?.data) {
              if (Array.isArray(response.data)) {
                allLanguages = response.data
              } else if (response.data.languages && Array.isArray(response.data.languages)) {
                allLanguages = response.data.languages
              } else {
                allLanguages = response.data
              }
            } else if (Array.isArray(response)) {
              allLanguages = response
            }
            // Filtrer pour ne garder que fr et en
            languages = allLanguages.filter(lang => 
              typeof lang === 'string' && SUPPORTED_LANGUAGES.some(supported => supported.code === lang)
            )
          } catch (err) {
            languages = []
          }
        }
        
        setAvailableTranslations(languages)
      } catch (err) {
        console.error('Error loading translations:', err)
        setAvailableTranslations([])
      } finally {
        setLoadingTranslations(false)
      }
    }

    loadTranslations()
  }, [entityId, entityType])

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const getEntityIcon = () => {
    if (selectedCourse) return BookOpen
    if (selectedModule) return FileText
    if (selectedLesson) return GraduationCap
    if (selectedBlock) return FileText
    if (selectedQuiz) return HelpCircle
    return Info
  }

  const getEntityTypeLabel = () => {
    if (selectedCourse) return 'Cours'
    if (selectedModule) return 'Module'
    if (selectedLesson) return 'Chapitre'
    if (selectedBlock) return 'Bloc'
    if (selectedQuiz) return 'Quiz'
    return 'Élément'
  }

  const getStatusBadge = (status) => {
    const statusMap = {
      'DRAFT': { label: 'Brouillon', color: 'bg-gray-100 text-gray-800' },
      'PUBLISHED': { label: 'Publié', color: 'bg-green-100 text-green-800' },
      'ARCHIVED': { label: 'Archivé', color: 'bg-red-100 text-red-800' }
    }
    const statusInfo = statusMap[status] || statusMap['DRAFT']
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    )
  }

  const getTranslationStatus = (langCode) => {
    const hasTranslation = availableTranslations.includes(langCode)
    const isOriginal = langCode === 'fr' // Supposons que FR est la langue par défaut
    
    if (isOriginal) {
      return { label: 'Original', color: 'text-blue-600', icon: CheckCircle }
    }
    
    if (hasTranslation) {
      return { label: 'Traduit', color: 'text-green-600', icon: CheckCircle }
    }
    
    return { label: 'Non traduit', color: 'text-gray-400', icon: AlertCircle }
  }

  // Actions disponibles selon le type
  const getAvailableActions = () => {
    if (selectedCourse) {
      return [
        { label: 'Ajouter un module', icon: Plus, onClick: onAddModule, variant: 'default' }
      ]
    }
    if (selectedModule) {
      return [
        { label: 'Ajouter une chapitre', icon: GraduationCap, onClick: () => onAddLesson && onAddLesson(), variant: 'default' },
        { label: 'Ajouter un quiz', icon: HelpCircle, onClick: onAddQuiz, variant: 'outline' }
      ]
    }
    if (selectedLesson) {
      return [
        { label: 'Ajouter un bloc texte', icon: FileText, onClick: () => onAddBlock && onAddBlock('TEXT'), variant: 'outline' },
        { label: 'Ajouter un bloc image', icon: ImageIcon, onClick: () => onAddBlock && onAddBlock('IMAGE'), variant: 'outline' },
        { label: 'Ajouter un bloc vidéo', icon: Video, onClick: () => onAddBlock && onAddBlock('VIDEO'), variant: 'outline' },
        { label: 'Ajouter un document téléchargeable', icon: Download, onClick: () => onAddBlock && onAddBlock('DOWNLOAD'), variant: 'outline' }
      ]
    }
    if (selectedBlock) {
      return []
    }
    if (selectedQuiz) {
      return [
        { label: 'Ajouter une question', icon: Plus, onClick: () => onEdit && onEdit(), variant: 'outline' }
      ]
    }
    return []
  }

  // Afficher un loader si un quiz, une leçon ou un bloc est en cours de chargement
  if ((isLoadingQuiz || isLoadingLesson || isLoadingBlock) && !activeEntity) {
    let loadingMessage = 'Chargement...'
    if (isLoadingQuiz) loadingMessage = 'Chargement du quiz...'
    else if (isLoadingLesson) loadingMessage = 'Chargement de la chapitre...'
    else if (isLoadingBlock) loadingMessage = 'Chargement du bloc...'
    
    return (
      <aside className="w-80 bg-white border-l border-gray-200 flex flex-col overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Inspecteur</h3>
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center text-gray-500">
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-[#141835] animate-spin" />
            <p className="font-medium">{loadingMessage}</p>
          </div>
        </div>
      </aside>
    )
  }

  if (!activeEntity) {
    return (
      <aside className="w-80 bg-white border-l border-gray-200 flex flex-col overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Inspecteur</h3>
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center text-gray-500">
            <Info className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="font-medium">Aucun élément sélectionné</p>
            <p className="text-sm mt-2">Sélectionnez un élément dans la hiérarchie pour voir ses détails</p>
          </div>
        </div>
      </aside>
    )
  }

  const EntityIcon = getEntityIcon()
  const availableActions = getAvailableActions()

  return (
    <aside className="w-80 bg-white border-l border-gray-200 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <EntityIcon className="w-5 h-5 text-[#141835]" />
            <h3 className="text-lg font-semibold text-gray-900">Inspecteur</h3>
          </div>
          {onClose && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
              title="Masquer l'inspecteur"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
          )}
        </div>
        <div className="text-xs text-gray-500">
          {getEntityTypeLabel()}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* A. Informations de base */}
        <div className="border-b border-gray-200">
          <button
            onClick={() => toggleSection('info')}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-gray-600" />
              <span className="font-medium text-gray-900">Informations</span>
            </div>
            {expandedSections.info ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </button>
          
          {expandedSections.info && (
            <div className="px-4 pb-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Nom / Titre</label>
                <div className="text-sm font-medium text-gray-900">{activeEntity.title || 'Sans titre'}</div>
              </div>
              
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Type</label>
                <div className="text-sm text-gray-700">{getEntityTypeLabel()}</div>
              </div>
              
              {activeEntity.status && (
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Statut</label>
                  <div>{getStatusBadge(activeEntity.status)}</div>
                </div>
              )}
              
              {activeEntity.language && (
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block flex items-center gap-1">
                    <Globe className="w-3 h-3" />
                    Langue principale
                  </label>
                  <div className="text-sm text-gray-700">
                    {SUPPORTED_LANGUAGES.find(l => l.code === activeEntity.language)?.name || activeEntity.language}
                  </div>
                </div>
              )}
              
              {activeEntity.createdAt && (
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Date de création
                  </label>
                  <div className="text-sm text-gray-700">
                    {new Date(activeEntity.createdAt).toLocaleDateString('fr-FR')}
                  </div>
                </div>
              )}
              
              {activeEntity.updatedAt && (
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Dernière mise à jour
                  </label>
                  <div className="text-sm text-gray-700">
                    {new Date(activeEntity.updatedAt).toLocaleDateString('fr-FR')}
                  </div>
                </div>
              )}
              
              {/* Informations spécifiques pour les quiz */}
              {entityType === 'quiz' && (
                <>
                  {activeEntity.description && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Description</label>
                      <div className="text-sm text-gray-700 line-clamp-3">{activeEntity.description}</div>
                    </div>
                  )}
                  {activeEntity.instructions && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Instructions</label>
                      <div className="text-sm text-gray-700 line-clamp-2">{activeEntity.instructions}</div>
                    </div>
                  )}
                  {activeEntity.questions && Array.isArray(activeEntity.questions) && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Questions</label>
                      <div className="text-sm text-gray-700">{activeEntity.questions.length} question(s)</div>
                    </div>
                  )}
                  {activeEntity.timeLimit && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Limite de temps
                      </label>
                      <div className="text-sm text-gray-700">{activeEntity.timeLimit} minutes</div>
                    </div>
                  )}
                  {activeEntity.passingScore !== undefined && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Score de passage</label>
                      <div className="text-sm text-gray-700">{activeEntity.passingScore}%</div>
                    </div>
                  )}
                </>
              )}
              
              {/* Informations spécifiques pour les modules */}
              {entityType === 'module' && activeEntity.description && (
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Description</label>
                  <div className="text-sm text-gray-700 line-clamp-3">{activeEntity.description}</div>
                </div>
              )}
              
              {/* Informations spécifiques pour les leçons */}
              {entityType === 'lesson' && (
                <>
                  {activeEntity.description && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Description</label>
                      <div className="text-sm text-gray-700 line-clamp-3">{activeEntity.description}</div>
                    </div>
                  )}
                  {activeEntity.duration && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Durée
                      </label>
                      <div className="text-sm text-gray-700">{activeEntity.duration} minutes</div>
                    </div>
                  )}
                  {activeEntity.blocks && Array.isArray(activeEntity.blocks) && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Blocs</label>
                      <div className="text-sm text-gray-700">{activeEntity.blocks.length} bloc(s)</div>
                    </div>
                  )}
                </>
              )}
              
              {/* Informations spécifiques pour les blocs */}
              {entityType === 'block' && (
                <>
                  {activeEntity.type && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Type de bloc</label>
                      <div className="text-sm text-gray-700">
                        {activeEntity.type === 'TEXT' ? 'Texte' : 
                         activeEntity.type === 'IMAGE' ? 'Image' : 
                         activeEntity.type === 'VIDEO' ? 'Vidéo' : 
                         activeEntity.type}
                      </div>
                    </div>
                  )}
                  {activeEntity.description && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Description</label>
                      <div className="text-sm text-gray-700 line-clamp-3">{activeEntity.description}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* B. Traductions */}
        <div className="border-b border-gray-200">
          <button
            onClick={() => toggleSection('translations')}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Languages className="w-4 h-4 text-gray-600" />
              <span className="font-medium text-gray-900">Traductions</span>
              {availableTranslations.length > 0 && (
                <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                  {availableTranslations.length}
                </span>
              )}
            </div>
            {expandedSections.translations ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </button>
          
          {expandedSections.translations && (
            <div className="px-4 pb-4 space-y-3">
              {/* Liste des traductions */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-500 block">Langues disponibles</label>
                <div className="space-y-1">
                  {SUPPORTED_LANGUAGES.map(lang => {
                    const status = getTranslationStatus(lang.code)
                    const StatusIcon = status.icon
                    const hasTranslation = availableTranslations.includes(lang.code)
                    
                    return (
                      <button
                        key={lang.code}
                        onClick={() => onShowTranslations && onShowTranslations(lang.code)}
                        className="w-full flex items-center justify-between p-2 bg-gray-50 rounded text-sm hover:bg-gray-100 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{lang.flag}</span>
                          <span className="text-gray-700">{lang.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusIcon className={`w-4 h-4 ${status.color}`} />
                          <span className={`text-xs ${status.color}`}>{status.label}</span>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* D. Actions rapides */}
        <div className="p-4 space-y-2 border-t border-gray-200">
          {/* Boutons Ajouter - toujours visibles */}
          {availableActions.length > 0 && (
            <div className="space-y-2 mb-4">
              {availableActions.map((action, index) => {
                const ActionIcon = action.icon
                return (
                  <Button
                    key={index}
                    variant={action.variant || 'outline'}
                    size="sm"
                    onClick={action.onClick}
                    className="w-full justify-start"
                  >
                    <ActionIcon className="w-4 h-4 mr-2" />
                    {action.label}
                  </Button>
                )
              })}
            </div>
          )}

          {/* Actions spécifiques pour le cours */}
          {selectedCourse && (
            <>
              {onEditImage && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onEditImage}
                  className="w-full"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Modifier l'image
                </Button>
              )}
              {onEditConfiguration && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onEditConfiguration}
                  className="w-full"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configuration
                </Button>
              )}
            </>
          )}
          
          {onEdit && !selectedCourse && (
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              className="w-full"
            >
              <Edit className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          )}
          
          {onDelete && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${activeEntity.title || 'cet élément'}" ? Cette action est irréversible.`)) {
                  onDelete()
                }
              }}
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          )}
        </div>
      </div>
    </aside>
  )
}

